import { escapeGraphQLCustomFieldStringArrayValue } from "@secureo/common/utils/commerceTools/escapeGraphQLCustomFieldValue"
import { encodeMatrixCustomField } from "@secureo/common/utils/commerceTools/escapeMatrixCustomField"

import { ProductConfiguration } from "@secureo/common/reducers/productDetailPageReducer"
import {
	TextLineItemDraft,
	MyShoppingListDraft,
	CustomFieldsDraft,
} from "@secureo/common/typings/generated/commercetools-graphql"
import { LockingSystemReducerState } from ".."
import { encodeConfigurationsByConfigurationGroup } from "../utils/escapeConfigurationsByConfigurationGroup"
import {
	LockingSystemComponent,
	LockingSystemComponentsByUuid,
} from "@secureo/common/typings/LockingSystem"

export interface MinimalConfiguration {
	sku: ProductConfiguration["sku"]
	quantity: ProductConfiguration["quantity"]
}

export interface MinimalConfigurationsBySku {
	[sku: string]: MinimalConfiguration
}

export interface MinimalConfigurationsByConfigurationGroup {
	[configurationGroupName: string]: MinimalConfigurationsBySku
}

export interface MinimalLockingSystemComponent {
	uuid: LockingSystemComponent["uuid"]
	sku: LockingSystemComponent["sku"]
	masterVariantSku: LockingSystemComponent["masterVariantSku"]
	name: LockingSystemComponent["name"]
	variantId: LockingSystemComponent["variantId"]
	quantity: LockingSystemComponent["quantity"]
	selectedSystem: LockingSystemComponent["selectedSystem"]
	selectedOuterLength: LockingSystemComponent["selectedOuterLength"]
	selectedInnerLength: LockingSystemComponent["selectedInnerLength"]
	configurationsByConfigurationGroup: MinimalConfigurationsByConfigurationGroup
}

const getShoppingListTextLineItemsFromMinimalLockingSystemComponents = (
	components: MinimalLockingSystemComponent[],
) => {
	const textLineItems = components.map((component) => {
		const {
			name,
			quantity,
			uuid,
			sku,
			masterVariantSku,
			variantId,
			selectedSystem,
			selectedOuterLength,
			selectedInnerLength,
			configurationsByConfigurationGroup,
		} = component

		const textLineItem: TextLineItemDraft = {
			name: [{ locale: "de", value: name }],
			quantity,
			custom: {
				type: {
					typeId: "type",
					key: "customTextLineItem",
				},
				fields: [
					{
						name: "uuid",
						value: `"${uuid}"`,
					},
					{
						name: "sku",
						value: `"${sku}"`,
					},
					{
						name: "masterVariantSku",
						value: `"${masterVariantSku}"`,
					},
					{
						name: "variantId",
						value: `${variantId}`,
					},
					{
						name: "selectedSystem",
						value: `"${selectedSystem ?? ""}"`,
					},
					{
						name: "selectedOuterLength",
						value: `${selectedOuterLength ?? 0}`,
					},
					{
						name: "selectedInnerLength",
						value: `${selectedInnerLength ?? 0}`,
					},
					{
						name: "configurationsByConfigurationGroup",
						value: `"${encodeConfigurationsByConfigurationGroup(
							configurationsByConfigurationGroup,
						)}"`,
					},
				],
			},
		}

		return textLineItem
	})

	return textLineItems
}

const getMinimalComponentsFromComponentsByUuid = (
	componentsByUuid: LockingSystemComponentsByUuid,
) => {
	const minimalComponents = Object.values(componentsByUuid).map((component) => {
		const {
			uuid,
			sku,
			masterVariantSku,
			name,
			variantId,
			quantity,
			selectedSystem,
			selectedOuterLength,
			selectedInnerLength,
			configurationsByConfigurationGroup,
		} = component

		const minimalConfigurationsByConfigurationGroup: MinimalConfigurationsByConfigurationGroup =
			Object.entries(configurationsByConfigurationGroup).reduce(
				(
					minimalConfigurationsByConfigurationGroup,
					[configurationGroupName, configurationsBySku],
				) => {
					const configurations = Object.values(configurationsBySku)
					if (configurations.length > 0) {
						const minimalConfigurationsBySku = configurations.reduce(
							(minimalConfigurationsBySku, configuration) => {
								const { sku, quantity } = configuration
								const minimalConfiguration: MinimalConfiguration = {
									sku,
									quantity,
								}

								return {
									...minimalConfigurationsBySku,
									[sku]: minimalConfiguration,
								}
							},
							{} as MinimalConfigurationsBySku,
						)

						return {
							...minimalConfigurationsByConfigurationGroup,
							[configurationGroupName]: minimalConfigurationsBySku,
						}
					}

					return minimalConfigurationsByConfigurationGroup
				},
				{} as MinimalConfigurationsByConfigurationGroup,
			)

		const minimalComponent: MinimalLockingSystemComponent = {
			uuid,
			sku,
			masterVariantSku,
			name,
			variantId,
			quantity,
			selectedSystem,
			selectedOuterLength,
			selectedInnerLength,
			configurationsByConfigurationGroup: minimalConfigurationsByConfigurationGroup,
		}

		return minimalComponent
	})

	return minimalComponents
}

export const getTextLineItemDraftsFromComponentsByUuid = (
	componentsByUuid: LockingSystemReducerState["componentsByUuid"],
) => {
	const minimalComponents = getMinimalComponentsFromComponentsByUuid(componentsByUuid)

	const textLineItemDrafts =
		getShoppingListTextLineItemsFromMinimalLockingSystemComponents(minimalComponents)

	return textLineItemDrafts
}

export const getLockingSystemShoppingListCustomFieldsDraft = (
	reducerState: LockingSystemReducerState,
) => {
	const { lockingComponentsUuids, keyComponentsUuids, matrix, system, quantity } = reducerState

	const customFieldsDraft: CustomFieldsDraft = {
		type: {
			typeId: "type",
			key: "customShoppingList",
		},
		fields: [
			{ name: "type", value: `"lockingSystem"` },
			{
				name: "lockingComponentsUuids",
				value: escapeGraphQLCustomFieldStringArrayValue(lockingComponentsUuids),
			},
			{
				name: "keyComponentsUuids",
				value: escapeGraphQLCustomFieldStringArrayValue(keyComponentsUuids),
			},
			{
				name: "lockingSystemCategoryKey",
				value: `"${system}"`,
			},
			{
				name: "lockingSystemCategoryQuantity",
				value: `${quantity}`,
			},
			{
				name: "matrix",
				value: encodeMatrixCustomField(matrix),
			},
		],
	}

	return customFieldsDraft
}

const getShoppingListDraftFromLockingSystemReducerState = (
	reducerState: LockingSystemReducerState,
) => {
	const { name, componentsByUuid } = reducerState

	const textLineItems = getTextLineItemDraftsFromComponentsByUuid(componentsByUuid)
	const custom = getLockingSystemShoppingListCustomFieldsDraft(reducerState)

	const draft: MyShoppingListDraft = {
		name: [
			{
				locale: "de",
				value: name,
			},
		],
		textLineItems,
		custom,
	}

	return draft
}

export default getShoppingListDraftFromLockingSystemReducerState
